import { useProjectInitDialog } from "@dayvise/react-app-core";
/**
 * A conversation, its runs and its branches (prompt.md §19, §20, §48, §49).
 *
 * This is the screen that has to make the platform's harder promises visible:
 * a run can continue while the tab is closed, several runs can be in flight at
 * once, steering changes a run without rewriting history, and a pause preserves
 * work. So the page shows the stream status, the branch a run is writing, and
 * every pending approval — rather than hiding them behind a spinner.
 */

import { useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ApprovalList,
  ClarificationPrompt,
  Select,
  Badge,
  BranchSwitcher,
  Button,
  Card,
  ConversationTimeline,
  ConversationOutputsPanel,
  DraftAttachmentList,
  DraftAttachmentPicker,
  type FormSubmission,
  RunDiffView,
  RunControls,
  RunStateBadge,
  Skeleton,
  StreamStatusIndicator,
  Switch,
  TextArea,
  TextField,
  rowStyle,
  stackStyle,
  MUTED_TEXT,
} from "@dayvise/react-ui";
import { isTerminal, type Approval, type RunId } from "@dayvise/domain";
import { OutputRollbackPanel, WorktreesStrip } from "@dayvise/react-app-core";
import { slashCompletions } from "@dayvise/client-sdk";
import {
  useConversation,
  useConversationRun,
  useDayviseClient,
  useFileDownload,
  TranscriptDownload,
  useLiveRun,
  useMessages,
  useRunApprovals,
  useRunDiff,
  useSendMessage,
  ProfileArchiveUpload,
  useActiveWorkspaceId,
  useSession,
  useReasoningDisplay,
  usePromptQueue,
  usePromptComposer,
  useAgentActivityDialog,
  conversationOutputViews,
  useConversationOutputs,
  useConversationOutputDownload,
  canSteerRun,
  useDraftAttachments,
  composerKeyDecision,
  insertComposerLineBelow,
  intentAvailability,
  intentControlLabel,
  queueLocationLabel,
  useTimestampMode,
  writeClipboardText,
  attachmentSendBlockedReason,
  describeAttachmentSelectionError,
  type DraftAttachmentSelection,
} from "@dayvise/react-app-core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { resolveApiOrigin, runStreamSocketUrl } from "../config.ts";
import { answerClientSlash, executeClientAction, type ClientActionContext } from "../clientSlash.ts";
import { streamTicketRefreshDelayMs } from "../streamTicket.ts";

export function ConversationDetailPage() {
  const { conversationId } = useParams({ from: "/c/$conversationId" });
  const workspaceId = useActiveWorkspaceId();
  return <ConversationDetail key={`${workspaceId}:${conversationId}`} conversationId={conversationId} />;
}

function ConversationDetail({ conversationId }: { readonly conversationId: string }) {
  const workspaceId = useActiveWorkspaceId();
  const client = useDayviseClient();
  const fileDownload = useFileDownload();
  const queryClient = useQueryClient();
  const { signOut, session } = useSession();
  const [showReasoning, setReasoningDisplay] = useReasoningDisplay("web", workspaceId, session?.account?.userId ?? null);

  const conversation = useConversation(conversationId as never);
  const [branchId, setBranchId] = useState<string | null>(null);
  const activeBranchId = branchId ?? conversation.data?.conversation.defaultBranchId ?? null;
  const agentActivity = useAgentActivityDialog(`${conversationId}:${activeBranchId}`);
  const projectInit = useProjectInitDialog(conversationId, activeBranchId);

  const messages = useMessages(conversationId as never, activeBranchId);
  const sendMessage = useSendMessage(conversationId as never);
  const outputs = useConversationOutputs(conversationId as never, activeBranchId);
  const outputDownload = useConversationOutputDownload(conversationId as never, activeBranchId);
  // Attachments belong to this conversation and branch only: switching either one
  // discards the pending selection rather than carrying bytes into another run.
  const draftAttachments = useDraftAttachments({ conversationId, branchId: activeBranchId ?? null });
  const [retrySend, setRetrySend] = useState<{ readonly text: string; readonly key: string; readonly workingDirectory: string; readonly attachments: readonly DraftAttachmentSelection[] } | null>(null);

  const [draft, setDraft] = useState("");
  const [steerDraft, setSteerDraft] = useState("");
  const { runId: activeRunId, selectRun: setActiveRunId, runs: availableRuns } = useConversationRun(conversationId, activeBranchId, conversation.data?.runs);
  // Sealed message secrets (secure text input, e.g. passwords). Values stay in
  // this component's state until send, are sealed server-side into the message
  // part, and are never sent to any model. Cleared the moment send succeeds.
  const [secretLabel, setSecretLabel] = useState("");
  const [secretValue, setSecretValue] = useState("");
  const [secrets, setSecrets] = useState<readonly { readonly label: string; readonly value: string; readonly reuse?: boolean }[]>([]);
  const [reuseSecret, setReuseSecret] = useState(false);
  const [clientNotice, setClientNotice] = useState<string | null>(null);
  const [diffFocus, setDiffFocus] = useState(false);
  const [rollbackOpen, setRollbackOpen] = useState(false);
  const [worktreesOpen, setWorktreesOpen] = useState(false);
  const diffAnchorRef = useRef<HTMLDivElement | null>(null);
  const [timestampMode, setTimestampMode] = useTimestampMode("web", workspaceId, session?.account?.userId ?? null);
  const scopeKey = `${workspaceId}:${conversationId}:${activeBranchId}`;
  const currentScope = useRef(scopeKey);
  currentScope.current = scopeKey;
  // Realtime socket ticket (report.md F-010): tickets live 60 seconds and a socket
  // upgrade carries one in the query string because browsers cannot header-auth an
  // upgrade. Refreshed *before* expiry so a reconnect never presents a stale
  // ticket; on failure the value drops to null, which switches the hook back to
  // the polling transport instead of looping on 401s.
  const [streamTicket, setStreamTicket] = useState<string | null>(null);
  useEffect(() => {
    if (activeRunId === null) {
      setStreamTicket(null);
      return;
    }
    let cancelled = false;
    let refreshTimer: ReturnType<typeof setTimeout> | undefined;
    const obtain = (): void => {
      client.runs
        .streamTicket(activeRunId)
        .then((issued) => {
          if (cancelled) return;
          setStreamTicket(issued.ticket);
          refreshTimer = setTimeout(obtain, streamTicketRefreshDelayMs(issued.expiresAt, Date.now()));
        })
        .catch(() => {
          // No ticket means no websocket: `websocketUrl` stays undefined and the
          // hook polls the durable replay endpoint instead.
          if (!cancelled) setStreamTicket(null);
        });
    };
    obtain();
    return () => {
      cancelled = true;
      if (refreshTimer !== undefined) clearTimeout(refreshTimer);
    };
  }, [client, activeRunId]);

  const transcriptMessageIds = useMemo(
    () => (messages.data?.messages ?? []).map((message) => message.messageId),
    [messages.data?.messages],
  );

  // Stable per ticket; the hook reads it through a ref, so it neither rebuilds the
  // transport nor reconnects when this identity changes on a refresh.
  const websocketUrl = useMemo(
    () =>
      streamTicket === null
        ? undefined
        : (runId: RunId, afterSequence: number) =>
            // The origin, not the versioned base: `runStreamSocketUrl` adds `/v1/...`
            // itself, so a versioned base here would request `/v1/v1/runs/...`.
            runStreamSocketUrl(resolveApiOrigin(), String(runId), afterSequence, streamTicket),
    [streamTicket],
  );

  const { live, status, snapshotRequired, resync } = useLiveRun({
    runId: activeRunId,
    conversationId: conversationId as never,
    transcriptMessageIds,
    websocketUrl,
  });
  const approvals = useRunApprovals(activeRunId);
  const runDiff = useRunDiff(activeRunId);

  const decide = useMutation({
    mutationFn: (input: { approval: Approval; decision: "approved" | "rejected" }) =>
      client.runs.decideApproval(input.approval.runId, input.approval.approvalId, input.decision),
    onSuccess: () => {
      if (activeRunId !== null) {
        void queryClient.invalidateQueries({ queryKey: ["workspace", workspaceId, "run", activeRunId, "approvals"] });
      }
    },
  });

  const submitClarification = useMutation({
    mutationFn: (input: { clarificationId: string; answers: Record<string, unknown> }) => {
      if (!activeRunId) throw new Error("No active run");
      return client.runs.answerClarification(activeRunId, input.clarificationId, input.answers);
    },
  });

  // A structured form answer may carry values the user protected: those are sealed
  // server-side, and the submission echoes the revision it answered.
  const submitClarificationForm = useMutation({
    mutationFn: (input: { clarificationId: string; submission: FormSubmission }) => {
      if (!activeRunId) throw new Error("No active run");
      return client.inputRequests.answerForm(activeRunId, input.clarificationId, {
        answers: input.submission.answers,
        protectedAnswers: input.submission.protectedAnswers.map((entry) => ({ fieldId: entry.fieldId, label: entry.label, value: entry.value })),
        ordinaryFieldIds: input.submission.ordinaryFieldIds,
        formRevision: input.submission.formRevision,
      });
    },
  });

  const declineClarification = useMutation({
    mutationFn: (clarificationId: string) => {
      if (!activeRunId) throw new Error("No active run");
      return client.inputRequests.decline(activeRunId, clarificationId);
    },
  });

  const navigate = useNavigate();

  const runState = live.runId === activeRunId ? live.state : availableRuns.find((run) => run.runId === activeRunId)?.state ?? null;
  const queue = usePromptQueue({
    scopeKey, workingDirectory: projectInit.workingDirectory, runId: activeRunId, runState,
    ready: !!conversation.data && activeBranchId !== null && !sendMessage.isPending,
    send: async (prompt) => {
      const outcome = await sendMessage.mutateAsync({ ...prompt, branchId: activeBranchId ?? undefined });
      if (!outcome.result) throw new Error("Send is not confirmed yet; retry to recover its result.");
      if (currentScope.current === scopeKey) {
        if (outcome.result?.branchId) {
          queue.adoptScope(`${workspaceId}:${conversationId}:${outcome.result.branchId}`);
          projectInit.adoptBranch(outcome.result.branchId);
          setBranchId(outcome.result.branchId);
        }
        if (outcome.result?.runId) setActiveRunId(outcome.result.runId);
        if (outcome.result.prefill !== undefined) setDraft(outcome.result.prefill);
      }
      return outcome.result?.runId ?? null;
    },
  });

  const { composePrompt, promptEditor } = usePromptComposer(JSON.stringify([session?.account?.userId, scopeKey]),
    (input) => sendMessage.mutateAsync({ ...input, workingDirectory: projectInit.workingDirectory, branchId: activeBranchId ?? undefined }),
    (outcome) => {
      if (outcome.result?.branchId) { projectInit.adoptBranch(outcome.result.branchId); setBranchId(outcome.result.branchId); }
      if (outcome.result?.runId) setActiveRunId(outcome.result.runId);
    },
    (seed) => setDraft(seed),
  );

  useEffect(() => {
    if (!diffFocus) return;
    diffAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setDiffFocus(false);
  }, [diffFocus]);

  /** The seams client-tier commands act through (task-3 parity). */
  const clientActions: ClientActionContext = {
    composePrompt: (seed) => {
      if (!activeBranchId) throw new Error("Wait for the conversation to load before composing a prompt.");
      setClientNotice(null);
      composePrompt(seed);
    },
    setReasoningDisplay,
    navigateTo: (to) => void navigate({ to }),
    openAgents: agentActivity.open,
    copyLastMessage: async () => {
      const reply = [...(messages.data?.messages ?? [])]
        .reverse()
        .find((message) => message.role === "assistant");
      const copied = (reply?.parts ?? [])
        .map((part) => (part.type === "text" ? part.text : ""))
        .join("");
      await navigator.clipboard.writeText(copied);
      setClientNotice("Copied the last reply to the clipboard.");
    },
    pasteFromClipboard: async () => {
      setDraft(await navigator.clipboard.readText());
    },
    startNewConversation: () => {
      void navigate({ to: "/" });
    },
    decideApproval: async (decision) => {
      if (!(approvals.data?.approvals ?? []).some((approval) => approval.status === "pending")) {
        throw new Error("No pending approvals in this run.");
      }
      for (const approval of approvals.data?.approvals ?? []) {
        if (approval.status !== "pending") continue;
        await client.runs.decideApproval(approval.runId, approval.approvalId, decision);
      }
      void queryClient.invalidateQueries({
        queryKey: ["workspace", workspaceId, "run", activeRunId, "approvals"],
      });
    },
    steer: async (instruction) => {
      if (!instruction.trim()) throw new Error("Usage: /steer <instruction>");
      if (!canSteerRun(activeRunId, runState)) throw new Error("No live run selected to steer.");
      await client.runs.steer(activeRunId!, instruction);
      setClientNotice("Steering instruction sent.");
    },
    queue: (instruction) => setClientNotice(queue.command(instruction)),
    send: (text) => {
      void sendText(text);
    },
    signOut: () => {
      void signOut();
    },
    refreshData: () => {
      void queryClient.invalidateQueries();
    },
    reloadPage: () => window.location.reload(),
    notify: setClientNotice,
    openDiff: () => {
      if (activeRunId === null) {
        setClientNotice("No run is selected; differences appear once a run has written files.");
        return;
      }
      setDiffFocus(true);
    },
    openRollback: () => setRollbackOpen(true),
    openWorktrees: () => setWorktreesOpen(true),
  };

  // The copy control writes what the message said; a refused or missing clipboard
  // is reported instead of silently doing nothing.
  const copyMessage = async (text: string): Promise<void> => {
    try {
      await writeClipboardText(text);
      setClientNotice("Copied the message to the clipboard.");
    } catch (error) {
      setClientNotice(error instanceof Error ? error.message : "The message could not be copied.");
      throw error;
    }
  };

  const onSend = async (): Promise<void> => {
    const text = draft.trim();
    if (text.length === 0) return;
    // Client-tier commands act here: no message is stored, no run starts.
    if (/^\/init(?:\s+scan)?$/.test(text)) {
      if (draftAttachments.hasAttachments || secrets.length > 0) {
        setClientNotice("Send or remove attachments before opening project instructions.");
        return;
      }
      setDraft("");
      projectInit.open();
      return;
    }
    const localCommand = answerClientSlash(text);
    if (localCommand !== null) {
      try {
        if (secrets.length > 0) throw new Error("Remove attached secrets before using a client command.");
        // A prose verb sends the rest of the line, so it carries the selected files.
        // Queueing or composing sends a different message later, which would leave the
        // selection behind, so those two are refused instead of silently ignoring it.
        if (draftAttachments.hasAttachments && (localCommand.action.kind === "queue" || localCommand.action.kind === "compose-prompt")) {
          throw new Error("Send or remove the attached files before queueing or composing a prompt.");
        }
        setDraft("");
        await executeClientAction(localCommand, clientActions);
      } catch (error) {
        setDraft(text);
        setClientNotice(error instanceof Error ? error.message : "Command failed.");
      }
      return;
    }
    setClientNotice(null);
    setDraft("");
    await sendText(text);
  };

  /** The one send path, shared by the composer and the prose-verb client action. */
  async function sendText(text: string, retry?: { readonly key: string; readonly workingDirectory: string; readonly attachments: readonly DraftAttachmentSelection[] }): Promise<void> {
    const blocking = attachmentSendBlockedReason({ pending: draftAttachments.pending, failed: draftAttachments.failed, count: draftAttachments.attachments.length });
    if (blocking !== null && retry === undefined) {
      setClientNotice(blocking);
      return;
    }
    const attached = retry === undefined ? secrets : [];
    // A retry replays the frozen selection and key, so a lost response can only
    // ever be the same send rather than a second run with newly picked files.
    const selection = retry === undefined ? draftAttachments.ready : retry.attachments;
    const idempotencyKey = retry?.key ?? crypto.randomUUID();
    if (retry === undefined) {
      setSecrets([]);
      setSecretLabel("");
      setSecretValue("");
    }
    setClientNotice(null);
    let outcome;
    try {
      outcome = await sendMessage.mutateAsync({
        text,
        workingDirectory: retry?.workingDirectory ?? projectInit.workingDirectory,
        branchId: activeBranchId ?? undefined,
        idempotencyKey,
        secrets: attached.length > 0 ? attached : undefined,
        attachments: selection.length > 0 ? selection : undefined,
      });
    } catch (error) {
      const problem = describeAttachmentSelectionError(error);
      if (problem !== null) setClientNotice(problem);
      if (selection.length > 0 || attached.length > 0) setRetrySend({ text, key: idempotencyKey, workingDirectory: retry?.workingDirectory ?? projectInit.workingDirectory, attachments: selection });
      setDraft((current) => (current.length === 0 ? text : current));
      return;
    }
    setRetrySend(null);
    if (retry === undefined) draftAttachments.clear();
    // A deduplicated retry returns the original result rather than a new run.
    // A server slash command answers with no run at all (runId null).
    if (outcome.result?.branchId) { projectInit.adoptBranch(outcome.result.branchId); setBranchId(outcome.result.branchId); }
    if (outcome.result?.prefill !== undefined) setDraft(outcome.result.prefill);
    const runId = outcome.result?.runId ?? undefined;
    if (runId !== undefined) setActiveRunId(runId);
  }

  const slashSuggestions = slashCompletions(draft);

  /** Enter sends, Ctrl+Enter inserts a line below, Alt+Enter queues, Ctrl+Alt+Enter steers. */
  async function steerActiveRun(instruction: string): Promise<void> {
    if (!activeRunId || instruction.length === 0) return;
    try {
      await client.runs.steer(activeRunId, instruction);
      setDraft("");
      setClientNotice(null);
    } catch (error) {
      setClientNotice(error instanceof Error ? error.message : "The instruction could not be sent to the run.");
    }
  }

  function onComposerKeyDown(event: ReactKeyboardEvent<HTMLTextAreaElement>): void {
    const state = {
      runActive: activeRunId !== null && live.state !== null,
      steerable: canSteerRun(activeRunId, runState),
      queueAvailable: true,
    };
    const decision = composerKeyDecision(
      {
        key: event.key,
        shiftKey: event.shiftKey,
        ctrlKey: event.ctrlKey,
        metaKey: event.metaKey,
        altKey: event.altKey,
        isComposing: event.nativeEvent.isComposing,
      },
      state,
    );
    if (!decision.handled) return;
    event.preventDefault();
    if (decision.newline) { insertComposerLineBelow(event.currentTarget, setDraft); return; }
    if (decision.refusal !== null) {
      setClientNotice(decision.refusal);
      return;
    }
    if (decision.intent === "send") { void onSend(); return; }
    if ((decision.intent === "queue" || decision.intent === "steer") && (draftAttachments.hasAttachments || secrets.length > 0 || secretValue.length > 0)) {
      setClientNotice("Queue and Steer accept text only. Send or remove the attached files and secure fields first.");
      return;
    }
    if (decision.intent === "queue") {
      const text = draft.trim();
      if (text.length === 0) return;
      setClientNotice(`${queue.command(text)} ${queueLocationLabel(true)}`);
      setDraft("");
      return;
    }
    if (decision.intent === "steer") {
      const text = draft.trim();
      if (text.length === 0) return;
      void steerActiveRun(text);
    }
  }

  const branches = conversation.data?.branches ?? [];

  return (
    <div style={stackStyle("var(--dayvise-space-md)")}>
      {promptEditor}
      <Button onClick={projectInit.open} disabled={!activeBranchId}>Project instructions ({projectInit.workingDirectory || "Project root"})…</Button>
      <header style={stackStyle("var(--dayvise-space-xs)")}>
        <h1 style={{ margin: 0, fontSize: "var(--dayvise-font-size-xl)" }}>
          {conversation.data?.conversation.title ?? "Conversation"}
        </h1>
        <div style={rowStyle("var(--dayvise-space-md)", "flex-start")}>
          {activeRunId !== null && !(live.state && isTerminal(live.state)) ? <StreamStatusIndicator
            status={status === "exhausted" ? "closed" : status === "idle" ? "connecting" : status}
            lastSequence={live.cursor >= 0 ? live.cursor : undefined}
          /> : null}
          {live.state ? <RunStateBadge state={live.state} pauseReason={live.pauseReason} showDetail /> : null}
          {live.usage.toolCalls > 0 ? (
            <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-xs)" }}>{live.usage.toolCalls} tool calls this run</span>
          ) : null}
        </div>
        {snapshotRequired ? (
          <div style={rowStyle("var(--dayvise-space-xs)", "flex-start")}>
            <span style={{ color: "var(--dayvise-color-warning)", fontSize: "var(--dayvise-font-size-sm)" }}>
              The event buffer no longer covers this client. Reloading the transcript.
            </span>
            <Button size="sm" onClick={resync}>
              Reload
            </Button>
          </div>
        ) : null}
      </header>

      {availableRuns.length > 1 ? (
        <Select label="Active run" value={activeRunId ?? ""}
          options={availableRuns.map((run) => ({ value: run.runId, label: `${run.state} — ${run.runId}` }))}
          onChange={(event) => setActiveRunId(event.target.value as RunId)} />
      ) : null}
      {branches.length > 1 ? (
        <BranchSwitcher branches={branches} activeBranchId={activeBranchId ?? ""} onSelect={setBranchId} />
      ) : null}

      {approvals.data && approvals.data.approvals.some((approval) => approval.status === "pending") ? (
        <ApprovalList
          approvals={approvals.data.approvals}
          busy={decide.isPending}
          onApprove={(approval) => decide.mutateAsync({ approval, decision: "approved" }).then(() => undefined)}
          onReject={(approval) => decide.mutateAsync({ approval, decision: "rejected" }).then(() => undefined)}
        />
      ) : null}

      {live.pendingClarifications.map((clarification) => {
        const staleSubmission = (submitClarificationForm.error as { code?: string } | null)?.code === "stale_form";
        return (
          <ClarificationPrompt key={clarification.clarificationId} clarification={clarification}
            busy={submitClarification.isPending || submitClarificationForm.isPending || declineClarification.isPending}
            error={submitClarification.error?.message ?? submitClarificationForm.error?.message}
            staleFromServer={staleSubmission}
            draftKey={`${workspaceId}:${session?.account?.userId ?? ""}:${activeRunId ?? ""}`}
            onSubmit={(clarificationId, answers) => submitClarification.mutateAsync({ clarificationId, answers }).then(() => undefined)}
            onSubmitForm={(submission) => submitClarificationForm.mutateAsync({ clarificationId: clarification.clarificationId, submission }).then(() => undefined)}
            onDecline={() => declineClarification.mutateAsync(clarification.clarificationId).then(() => undefined)}
            onRefresh={() => queryClient.invalidateQueries().then(() => undefined)} />
        );
      })}
      <div style={{ flex: 1, minHeight: "320px", display: "flex", flexDirection: "column" }}>
        <TranscriptDownload client={client} scopeKey={`${workspaceId}:${session?.account?.userId ?? ""}`} conversationId={conversationId} branchId={activeBranchId} />
        {fileDownload.error ? <span role="alert">{fileDownload.error}</span> : null}
        {messages.isPending ? (
          <Skeleton lines={5} />
        ) : (
          <ConversationTimeline
        timestampMode={timestampMode}
            showReasoning={showReasoning}
            onDownloadFile={(fileId) => void fileDownload.download(fileId)}
            downloadingFileId={fileDownload.downloadingFileId}
            messages={messages.data?.messages ?? []}
            live={activeRunId === null || live.streamingText.length === 0 ? null : { text: live.streamingText, runId: activeRunId }}
            loading={messages.isPending}
            onCopyMessage={copyMessage}
          />
        )}
      </div>

      {activeBranchId !== null ? (
        <ConversationOutputsPanel
          outputs={conversationOutputViews(outputs.data?.tree)}
          branchLabel={activeBranchId}
          truncated={outputs.data?.tree.truncated ?? false}
          loading={outputs.isPending}
          error={outputs.error instanceof Error ? outputs.error.message : null}
          downloadingFileId={outputDownload.downloadingFileId}
          downloadError={outputDownload.error}
          onRefresh={() => void outputs.refetch()}
          onDownload={(entry) => void outputDownload.download(entry)}
        />
      ) : null}

      {activeRunId !== null ? (
        <Card title="Run controls" subtitle="Steering changes what the current run does next; it never edits history.">
          <RunControls
            state={live.state ?? "running"}
            pauseReason={live.pauseReason}
            busy={false}
            steerDraft={steerDraft}
            onSteerDraftChange={setSteerDraft}
            onSteer={async (message) => {
              await client.runs.steer(activeRunId, message);
              setSteerDraft("");
            }}
            onPause={async () => {
              await client.runs.pause(activeRunId, "user_requested");
            }}
            onResume={async () => {
              await client.runs.resume(activeRunId, null);
            }}
            onCancel={async () => {
              await client.runs.cancel(activeRunId, "user_requested");
            }}
          />
        </Card>
      ) : null}

      <div ref={diffAnchorRef} />

      {activeRunId !== null ? (
        <RunDiffView
          files={runDiff.data?.diff.files ?? []}
          loading={runDiff.isPending}
          error={runDiff.error instanceof Error ? runDiff.error.message : null}
          unpublished={runDiff.data?.diff.unpublished ?? false}
          truncated={runDiff.data?.diff.truncated ?? false}
        />
      ) : null}

      {rollbackOpen ? (
        <OutputRollbackPanel conversationId={conversationId} branchId={activeBranchId} />
      ) : null}
      {worktreesOpen ? <WorktreesStrip conversationId={conversationId} branchId={activeBranchId} /> : null}
      {!rollbackOpen ? (
        <div>
          <Button onClick={() => setRollbackOpen(true)}>Roll back written files…</Button>
        </div>
      ) : null}

      <div style={stackStyle("var(--dayvise-space-xs)")}>
        <ProfileArchiveUpload conversationId={conversationId} disabled={conversation.data?.conversation.visibility !== "private"} onCommand={setDraft} />
        <DraftAttachmentList
          attachments={draftAttachments.attachments}
          rejected={draftAttachments.rejected}
          disabled={sendMessage.isPending}
          onRemove={draftAttachments.remove}
          onRetry={draftAttachments.retry}
          onDismissRejected={draftAttachments.dismissRejected}
        />
        <TextArea
          label="Message"
          rows={3}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={onComposerKeyDown}
          hint="Enter sends, Ctrl+Enter inserts a line below, Shift+Enter adds a newline, Alt+Enter queues for the active run, Ctrl+Alt+Enter steers it. Selected images and text files ride with the message and are refused, never dropped, when the selected model cannot read them. Slash commands (/help, /tools, /skills, /status, /usage, /todos, /plan) answer faster — most without starting one."
        />
        {agentActivity.dialog}
        {projectInit.dialog}
        <Button size="sm" variant="ghost" disabled={!activeBranchId || secrets.length > 0 || draftAttachments.hasAttachments}
          onClick={() => { composePrompt(draft); setDraft(""); }}>Compose prompt</Button>
        {slashSuggestions.length > 0 ? (
          <div style={rowStyle("var(--dayvise-space-xs)", "flex-start")}>
            {slashSuggestions.map((command) => (
              <Button
                key={command.name}
                size="sm"
                variant="ghost"
                onClick={() => setDraft(`/${command.name}${command.argsHint ? " " : ""}`)}
              >
                /{command.name}{command.argsHint ? <> <code>{command.argsHint}</code></> : null} — {command.description}
              </Button>
            ))}
          </div>
        ) : null}
        {queue.count > 0 && <span style={MUTED_TEXT}>{queue.count} prompt(s) queued locally. {queue.error ? `Failed: ${queue.error}. Use /queue retry or /queue clear.` : "Keep this conversation open."}</span>}
        {clientNotice !== null ? (
          <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-sm)" }}>{clientNotice}</span>
        ) : null}
        {secrets.length > 0 ? (
          <div style={rowStyle("var(--dayvise-space-xs)", "flex-start")}>
            {secrets.map((secret) => (
              <span key={secret.label} style={rowStyle("var(--dayvise-space-xs)", "flex-start")}>
                <Badge tone="neutral">{secret.reuse === true ? `secret: ${secret.label} · saved for reuse` : `secret: ${secret.label}`}</Badge>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSecrets((previous) => previous.filter((entry) => entry.label !== secret.label))}
                >
                  Remove
                </Button>
              </span>
            ))}
          </div>
        ) : null}
        <details>
          <summary style={{ cursor: "pointer", ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-sm)" }}>
            Attach a secret (passwords stay sealed — never sent to any model)
          </summary>
          <div style={stackStyle("var(--dayvise-space-xs)")}>
            <TextField label="Secret label" value={secretLabel} onChange={(event) => setSecretLabel(event.target.value)} placeholder="db password" />
            <TextField
              label="Secret value"
              secret
              value={secretValue}
              onChange={(event) => setSecretValue(event.target.value)}
              hint="Sealed on send. The agent can use it in tools that authenticate; it can never store it or show it."
            />
            <Switch
              label="Save for reuse"
              description="Also store this value as a personal credential in Providers, where you can revoke it. Leave it off to keep the secret scoped to this task only."
              checked={reuseSecret}
              onChange={setReuseSecret}
            />
            <div style={rowStyle("var(--dayvise-space-xs)")}>
              <Button
                size="sm"
                variant="secondary"
                disabled={secretLabel.trim().length === 0 || secretValue.length === 0 || secrets.length >= 10}
                onClick={() => {
                  setSecrets((previous) => [...previous, { label: secretLabel.trim(), value: secretValue, ...(reuseSecret ? { reuse: true } : {}) }]);
                  setSecretLabel("");
                  setSecretValue("");
                  setReuseSecret(false);
                }}
              >
                Attach secret
              </Button>
            </div>
          </div>
        </details>
        <div style={rowStyle("var(--dayvise-space-xs)")}>
          <DraftAttachmentPicker disabled={!activeBranchId || sendMessage.isPending} onFiles={draftAttachments.addFiles} />
          <Button
            variant="primary"
            loading={sendMessage.isPending}
            disabled={draft.trim().length === 0 || draftAttachments.pending || draftAttachments.failed > 0}
            title={intentControlLabel("send", intentAvailability({ runActive: activeRunId !== null && live.state !== null, steerable: canSteerRun(activeRunId, runState) }))}
            onClick={() => void onSend()}
          >
            Send
          </Button>
          <Select
            label="Timestamps"
            value={timestampMode}
            onChange={(event) => setTimestampMode(event.target.value as never)}
            options={[{ value: "relative", label: "Relative" }, { value: "absolute", label: "Absolute" }, { value: "hidden", label: "Hidden" }]}
          />
        </div>
        {sendMessage.error || retrySend !== null ? (
          <span style={{ color: "var(--dayvise-color-danger)", fontSize: "var(--dayvise-font-size-sm)" }}>
            {sendMessage.error instanceof Error ? sendMessage.error.message : retrySend !== null ? "The message was not confirmed." : "the message could not be sent"}
            {retrySend !== null ? (
              <Button size="sm" variant="ghost" loading={sendMessage.isPending} onClick={() => void sendText(retrySend.text, retrySend)}>Retry send</Button>
            ) : null}
          </span>
        ) : null}
      </div>
    </div>
  );
}
